/**
 * Web端 声网SDK对接
 * @author lianghua.fu
 * @date 2025-02-26 17:42:03
 */
import { UiToast } from 'components';

import { debug, globalPageConfig, config, request } from 'lib';
import AgoraUserWeb from './agora-user-web';
import RtcBase, {
  DeviceInfo,
  EncoderConfig,
  IOptions,
  IPreviewOptions,
  IRtcBase,
  RoomTypeName,
} from './rtc-base';

const log = debug.create('RtcAgoraWeb');
const error = debug.create('RtcAgoraWeb:error');

const playVideos = {};
const userClients = {};
const userMute = {};

export default class RtcAgora extends RtcBase implements IRtcBase {
  rtcType = 'agora';

  streamType: string;

  musicInstance: any;

  client: any;

  pushRtmpUrl = '';

  streamId = '';

  rtc: any = {
    localAudioTrack: null,
    localVideoTrack: null,
    isEnableVideo: false,
    isEnableAudio: false,
  };

  videoEncoderConfig: any;

  _setUser(uid) {
    if (this.options.userId !== uid) {
      this.options.userId = uid;
      this.options.nickName = `匿名用户-${uid}`;
    }
  }

  init(options: IOptions) {
    this.options = options;
    this.trackName = `RtcAgoraWeb_${this.options.roomTypeName}`;

    this.trackLog('init', options);

    try {
    } catch (e) {}

    this._initClient();
    if (this.isRoomOwner()) {
      this.onLogin(true);
    } else {
      this._loginClient();
    }
  }

  _initClient() {
    // 开启日志上传功能
    window.AgoraRTC.enableLogUpload();
    // 将日志输出级别设置为 INFO
    window.AgoraRTC.setLogLevel(1);
    // 创建声网实例
    this.client = window.AgoraRTC.createClient({
      mode: 'live',
      // codec: 'h264',
      codec: 'vp8',
    });
    // 开启音频监视器
    this.client.enableAudioVolumeIndicator();
    this.trackLog('initClient');

    this.client.on('connection-state-change', (curState, revState, reason) => {
      this.trackLog('connection-state-change', { curState, revState, reason });
      if (curState === 'CONNECTED') {
        this.onLogin(true);
      }
    });

    this.client.on('user-published', async (user, mediaType) => {
      this.trackLog('user-published', user.uid, mediaType);

      await this.client.subscribe(user, mediaType);

      if (mediaType === 'video') {
        const videoDom = playVideos[user.uid];
        if (videoDom) {
          user.videoTrack.play(videoDom);
          this.trackLog('user-published-play-video', user.uid);
        } else {
          this.trackLog('user-published-play-video-error', user.uid);
        }
      }

      if (mediaType === 'audio') {
        this.trackLog('user-published-play-audio', user.uid);
        user.audioTrack.play();
      }
    });

    this.client.on('user-unpublished', async (user, mediaType) => {
      this.trackLog('user-unpublished', user.uid);
      await this.client.unsubscribe(user, mediaType);
    });

    this.client.on('volume-indicator', (result) => {
      const soundLevelList = result.map((item) => {
        return {
          streamId: `${item.uid}`,
          soundLevel: item.level,
        };
      });

      this.options.onSoundLevelUpdate?.(soundLevelList);
    });

    this.client.on('token-privilege-will-expire', async (event) => {
      this.trackLog('token-privilege-will-expire', event);
      let token;
      if (this.isRoomOwner()) {
        token = await this._getPublishToken();
      } else {
        token = await this._getChannelToken(this.options.roomId);
      }
      await this.client.renewToken(token);
    });
    // token 已过期
    this.client.on('token-privilege-did-expire', async (event) => {
      this.trackLog('token-privilege-will-expire', event);
      let token;
      if (this.isRoomOwner()) {
        token = await this._getPublishToken();
      } else {
        token = await this._getChannelToken(this.options.roomId);
      }
      await this.client.join(
        config.agoraAppId,
        this.options.roomId,
        token,
        parseInt(this.options.userId, 10),
      );
      this.onLogin(true);
    });

    this.client.on('exception', (event) => {
      this.trackLog('exception', event);
    });
  }

  async _loginClient() {
    // 获取频道拉流Token
    let token;
    if (this.isRoomOwner()) {
      token = await this._getPublishToken();
      this.client.setClientRole('host');
    } else {
      token = await this._getChannelToken(this.options.roomId);
    }

    const res = await this.client.join(
      config.agoraAppId,
      this.options.roomId,
      token,
      parseInt(this.options.userId, 10),
    );
    this.onLogin(true);
    this.trackLog('loginClient', { res });
  }

  async _getChannelToken(roomId) {
    const res: any = await request.cryolite.post('/api/v1/liveRoom/enter', {
      room_ids: [roomId],
    });

    this.trackLog('getChannelToken', { roomId, res });
    const token = res.data.token_map[roomId].room_token;
    this._setUser(`${res.data.user_id}`);
    return token;
  }

  async _getPublishToken() {
    if (this.options.roomTypeName === RoomTypeName.party) {
      const res: any = await request.funny.get('api/v1/multiRoom/token');
      this.trackLog('getPublishToken', { res, type: 'multiRoom' });
      return res.data.roomToken;
    }
    const res: any = await request.cryolite.post('/api/v1/liveRoom/start');
    this.trackLog('getPublishToken', { res, type: 'liveRoom' });
    return res.data.room_token;
  }

  async getDeviceList(): Promise<{
    cameras: DeviceInfo[];
    microphones: DeviceInfo[];
  }> {
    let result = await Promise.all([
      window.AgoraRTC.getCameras(),
      window.AgoraRTC.getMicrophones(),
    ]);
    const [cameras, microphones] = result;
    this.trackLog('getDeviceList', {
      cameras,
      microphones,
    });
    return {
      cameras: cameras.map((item) => {
        return {
          deviceName: item.label,
          deviceId: item.deviceId,
        };
      }),
      microphones: microphones.map((item) => {
        return {
          deviceName: item.label,
          deviceId: item.deviceId,
        };
      }),
    };
  }

  // 设置推流采集配置
  // async setVideoEncoderConfig(previewConfig) {
  //   const encoderConfig = this.getEncoderConfig(previewConfig);
  //   const setEncoderRes = this.client.setVideoEncoderConfiguration(
  //     encoderConfig.video,
  //   );
  //
  //   this.trackLog('set_video_encoder_config', {
  //     encoderConfig: encoderConfig.video,
  //     setEncoderRes,
  //   });
  // }

  _getAgoraEncoderConfig(streamType, videoInputName) {
    const encoderConfig = this.getEncoderConfig(streamType, videoInputName);

    this.videoEncoderConfig = {
      bitrateMin: encoderConfig.bitRate?.minBitRate,
      bitrateMax: encoderConfig.bitRate?.maxBitRate,
      frameRate: encoderConfig.frameRate,
      height: encoderConfig.height,
      width: encoderConfig.width,
    };

    return {
      video: {
        encoderConfig: this.videoEncoderConfig,
      },
      audio: {
        AEC: encoderConfig.echoCancellation, // 是否开启回声消除
        AGC: encoderConfig.autoGainControl, // 是否开启自动增益：
        ANS: encoderConfig.noiseSuppression, // 是否开启噪声抑制：
        encoderConfig: {
          // 音频码率
          bitrate: encoderConfig.audioBitRate, // 音频码率，单位为 Kbps。
          // stereo: // 是否开启立体声
          // sampleSize: // 音频采样大小。
          // sampleRate: //  音频采样率，单位为 Hz。
        },
      },
    };
  }

  // 是否启用 音频的 3A 优化
  _toggleAudio3A(toggle = true) {
    const audioAecRes = this.client.setParameters(
      JSON.stringify({ 'che.audio.enable.aec': toggle }),
    );
    const audioNsRes = this.client.setParameters(JSON.stringify({ 'che.audio.enable.ns': toggle }));
    const audioAgcRes = this.client.setParameters(
      JSON.stringify({ 'che.audio.enable.agc': toggle }),
    );
  }

  async startPreview(opts: IPreviewOptions, streamType) {
    const { viewContainer, hasVideo, videoInput, videoInputName, audioInput, success, error } =
      opts;

    super.startPreview(opts, streamType);
    this.videoInputName = videoInputName;
    // this.stopPreview()

    log('startPreview start', opts, streamType);

    const encoderConfig = this._getAgoraEncoderConfig(streamType, videoInputName);

    const video = viewContainer.querySelector('video');
    this.viewVideo = video!;

    try {
      if (audioInput) {
        // 采集音频（不需要播放）
        this.rtc.localAudioTrack = await window.AgoraRTC.createMicrophoneAudioTrack({
          ...encoderConfig.audio,
          microphoneId: audioInput,
        });

        log('rtc.localAudioTrack', this.rtc.localAudioTrack);
      }

      if (videoInput) {
        // 采集视频
        this.rtc.localVideoTrack = await window.AgoraRTC.createCameraVideoTrack({
          ...encoderConfig.video,
          cameraId: videoInput,
        });
        log('rtc.localVideoTrack', this.rtc.localVideoTrack);
        // 播放视频
        this.rtc.localVideoTrack.play(video, { mirror: false });
      }

      this.trackLog('startPreview', {
        audioInput,
        videoInput,
        encoderConfig,
      });

      this.onPreviewed(true);
      success?.();
    } catch (err) {
      console.error('startPreview', err);
      error?.();
    }
  }

  rePreview(viewContainer: HTMLDivElement) {
    if (this.isPreviewed) {
      let video = viewContainer.querySelector('video');
      this.rtc?.localVideoTrack?.play(video, { mirror: false });
      this.trackLog('rePreview');
    }
  }

  stopPreview() {
    if (this.isPreviewed) {
      this.onPreviewed(false);
      this.rtc.localVideoTrack?.stop();
      this.trackLog('stopPreview');
    }
  }

  async enableCamera(enable: boolean) {
    if (!this.isPreviewed) {
      log('enableCamera not previewed');
      return;
    }
    if (this.rtc?.isEnableVideo === enable) {
      return;
    }
    await this.rtc.localVideoTrack?.setMuted(!enable);
    this.rtc.isEnableVideo = enable;
    this.trackLog('enableCamera', { enable });
  }

  async enableMicrophone(enable: boolean) {
    if (!this.isPreviewed) {
      log('enableMicrophone not previewed');
      return;
    }
    if (this.rtc?.isEnableAudio === enable) {
      return;
    }
    await this.rtc.localAudioTrack?.setMuted(!enable);
    this.rtc.isEnableAudio = enable;
    this.trackLog('enableMicrophone', {
      enable,
    });
  }

  async startPublishingStream() {
    if (this.isLogin) {
      try {
        const token = await this._getPublishToken();
        if (this.isRoomOwner()) {
          const res = await this.client.join(
            config.agoraAppId,
            this.options.roomId,
            token,
            parseInt(this.options.userId, 10),
          );
          this.trackLog('joinChannel', {
            res,
          });
        } else {
          await this.client.renewToken(token);
        }
        await this.client.setClientRole('host');

        const publishTracks = [this.rtc.localAudioTrack, this.rtc.localVideoTrack].filter(
          (track) => {
            return !!track;
          },
        );
        await this.client.publish(publishTracks);

        this.isPublishing = true;
        this.options.onPublishStateUpdate?.(true);
        this.trackLog('startPublishingStream');

        // this.options.onPublishStateUpdate?.(true, this.options.streamId);
      } catch (err) {
        this.trackLog('startPublishingStreamError', {
          err,
          desc: '推流状态异常',
        });
        console.error(err);
        UiToast.fail('推流状态异常');
      }
    } else {
      UiToast.fail('未登录，推流状态异常');
    }
  }

  async stopPublishingStream() {
    if (!this.isPublishing) {
      return;
    }

    // this.stopPreview();

    this.rtc.localAudioTrack?.close();
    this.rtc.localVideoTrack?.close();
    const publishTracks = [this.rtc.localAudioTrack, this.rtc.localVideoTrack].filter((track) => {
      return !!track;
    });
    if (publishTracks.length) {
      await this.client.unpublish(publishTracks);
    }

    this.trackLog('stopPublishingStream');
    this.isPublishing = false;

    this.options.onStopPublishingStream?.();
    if (!this.isRoomOwner()) {
      await this.client?.setClientRole('audience', { level: 1 });
      // 切回拉流token
      const token = await this._getChannelToken(this.options.roomId);
      this.client?.renewToken(token);
    } else {
      this.client.leave();
      this.trackLog('leaveChannel');
    }
  }

  startPlayingStream(streamId: string, viewContainer: HTMLDivElement, onFail, roomId: string) {
    const userId = parseInt(streamId, 10);

    const video = viewContainer.querySelector('video');

    // 不在同一个房间的拉流
    if (roomId && roomId !== this.options.roomId) {
      const userClient = new AgoraUserWeb(
        this.options.userId, // 当前用户自己的id
        roomId,
        video,
      );
      this.trackLog(
        'startPlayingStream',
        {
          streamId,
          roomId,
        },
        userMute,
      );
      if (userMute[roomId] === false) {
        userClient.mutePlayStreamAudio(streamId, false);
      }
      userClients[roomId] = userClient;
      return async () => {
        delete userClients[roomId];
        delete userMute[roomId];
        const res = await userClient.leave();
        this.trackLog('stopPlayingStream', {
          res,
        });
      };
    }

    const remoteUser = this.client.remoteUsers.find((item) => {
      return item.uid === userId;
    });

    // 正常房间内播放视频的情况
    playVideos[userId] = video;
    if (remoteUser?.videoTrack) {
      remoteUser?.videoTrack.play(video);
      this.trackLog('startPlayingStream', {
        streamId,
        roomId,
      });
    } else {
      error('startPlayingStream no video track');
    }
    return () => {
      this._stopPlayingStream(userId, roomId);
    };
  }

  // 停止播放远端流
  _stopPlayingStream(userId, roomId) {
    const remoteUser = this.client.remoteUsers.find((item) => {
      return item.uid === userId;
    });

    remoteUser?.videoTrack?.stop();
    playVideos[userId] = null;

    this.trackLog('stopPlayingStream', {
      userId,
    });
  }

  mutePlayStreamAudio(streamId: string, mute: boolean, video: HTMLVideoElement) {
    video.muted = !mute;

    userMute[parseInt(streamId, 10)] = mute;

    const userClient = userClients[parseInt(streamId, 10)];
    if (userClient) {
      userClient.mutePlayStreamAudio(streamId, mute);
    } else {
      const remoteUser = this.client.remoteUsers.find((item) => {
        return item.uid === parseInt(streamId, 10);
      });
      remoteUser?.audioTrack?.setVolume(!mute ? 0 : 100);
    }
    this.trackLog('mutePlayStreamAudio', { streamId, mute });
  }

  updateStream(streamType: string, isJoinLive: boolean) {
    if (!this.isPublishing) {
      this.trackLog('updateStream', 'not publishing');
      return;
    }
    super.updateStream(streamType, isJoinLive);
    this.streamType = streamType;

    const encoderConfig = this._getAgoraEncoderConfig(streamType, this.videoInputName);
    this.rtc.localVideoTrack?.setEncoderConfiguration(encoderConfig.video);
    this.trackLog('updateStream', {
      streamType,
      isJoinLive,
      encoderConfig,
      userId: this.options.userId,
    });
  }

  destroy() {
    this.stopPreview();
    this.onLogin(false);
    this.rtc.localAudioTrack?.close();
    this.rtc.localVideoTrack?.close();
    this.client?.leave();

    this.trackLog('destroy', '引擎销毁');
  }
}
